import { useState, useCallback, useRef } from 'react';
import { financialTransactionApi } from '@/services/api/financialTransactionApi';
import type { FinancialTransaction } from '@/models/FinancialTransaction';

export const useTransactionExpansion = () => {
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
  const [childrenCache, setChildrenCache] = useState<Map<number, FinancialTransaction[]>>(new Map());
  const [loadingRows, setLoadingRows] = useState<Set<number>>(new Set());
  const fetchedTransactionsRef = useRef(new Set<number>());

  // Helper function to recursively cache all children
  const cacheAllChildren = useCallback((parentId: number, children: any[], cache: Map<number, any[]>) => {
    if (!children || children.length === 0) return;

    // Cache direct children
    cache.set(parentId, children);

    // Recursively cache grandchildren
    for (const child of children) {
      if (child.children && child.children.length > 0) {
        cacheAllChildren(child.id, child.children, cache);
      }
    }
  }, []);

  const toggleRowExpansion = useCallback(async (txnId: number) => {
    console.log(`[toggleRowExpansion] Called for transaction ${txnId}`);

    const isExpanded = expandedRows.has(txnId);

    if (isExpanded) {
      // Collapse the row
      console.log(`[toggleRowExpansion] Collapsing transaction ${txnId}`);
      setExpandedRows(prev => {
        const newSet = new Set(prev);
        newSet.delete(txnId);
        return newSet;
      });
    } else {
      // Check if children are already cached
      const cachedChildren = childrenCache.get(txnId);
      if (cachedChildren && cachedChildren.length > 0) {
        console.log(`[toggleRowExpansion] Cached children for ${txnId}:`, cachedChildren);
        setExpandedRows(prev => new Set(prev).add(txnId));
        return;
      }

      // Expand the row immediately so skeleton shows
      console.log(`[toggleRowExpansion] Expanding transaction ${txnId}`);
      setExpandedRows(prev => new Set(prev).add(txnId));

      // Fetch children if not in cache and not already being fetched
      if (!fetchedTransactionsRef.current.has(txnId)) {
        console.log(`[toggleRowExpansion] Fetching children for transaction ${txnId}...`);
        fetchedTransactionsRef.current.add(txnId);

        // Set loading state immediately so skeleton shows
        setLoadingRows(prev => new Set(prev).add(txnId));

        try {
          const txnWithChildren = await financialTransactionApi.getTransaction(txnId, true, false);
          console.log(`[toggleRowExpansion] API response for ${txnId}:`, txnWithChildren);

          if (txnWithChildren.children && txnWithChildren.children.length > 0) {
            console.log(`[toggleRowExpansion] Transaction ${txnId} has ${txnWithChildren.children.length} children:`, txnWithChildren.children.map(c => c.id));

            setChildrenCache(prevCache => {
              const newCache = new Map(prevCache);
              cacheAllChildren(txnId, txnWithChildren.children || [], newCache);
              return newCache;
            });

            // Fetch grandchildren for each child to ensure we have the full hierarchy
            for (const child of txnWithChildren.children) {
              if (!fetchedTransactionsRef.current.has(child.id)) {
                console.log(`[toggleRowExpansion] Fetching grandchildren for child ${child.id}...`);
                fetchedTransactionsRef.current.add(child.id);
                try {
                  const childWithGrandchildren = await financialTransactionApi.getTransaction(child.id, true, false);
                  if (childWithGrandchildren.children && childWithGrandchildren.children.length > 0) {
                    console.log(`[toggleRowExpansion] Child ${child.id} has ${childWithGrandchildren.children.length} grandchildren:`, childWithGrandchildren.children.map(gc => gc.id));
                    setChildrenCache(prevCache => {
                      const newCache = new Map(prevCache);
                      cacheAllChildren(child.id, childWithGrandchildren.children || [], newCache);
                      return newCache;
                    });
                  } else {
                    console.log(`[toggleRowExpansion] Child ${child.id} has no grandchildren`);
                  }
                } catch (error) {
                  console.error(`[toggleRowExpansion] Error fetching grandchildren for ${child.id}:`, error);
                }
              }
            }

            console.log(`[toggleRowExpansion] Completed fetching hierarchy for transaction ${txnId}`);
          } else {
            console.log(`[toggleRowExpansion] Transaction ${txnId} has no children`);
          }
        } catch (error) {
          console.error(`[toggleRowExpansion] Error fetching children for ${txnId}:`, error);
        } finally {
          // Clear loading state
          setLoadingRows(prev => {
            const newSet = new Set(prev);
            newSet.delete(txnId);
            return newSet;
          });
        }
      }
    }
  }, [expandedRows, childrenCache, cacheAllChildren]);

  return {
    expandedRows,
    childrenCache,
    loadingRows,
    toggleRowExpansion,
    setChildrenCache,
    cacheAllChildren,
  };
};
